import 'classlist-polyfill';
import 'focus-visible';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { ButtonHTMLAttributes, useCallback, useMemo } from 'react';

import styles from './styles.module.scss';
import Icon, { ICON_SIZES } from '../Icon/index.ts';
import type { InferPropTypes } from '../typings/index.ts';
import addVariants from '../utils/addVariants/index.ts';
import useBoolean from '../utils/useBoolean/index.ts';
import useEventCallback from '../utils/useEventCallback/index.ts';
import useSpacing from '../utils/useSpacing/index.ts';

export const COLORS = [
  'white',
  'blue',
  'dark-blue',
  'gray',
  'black',
  'light',
  'transparent',
  'red',
  'green',
  'orange',
  'yellow',
  'blue-hover',
  'dark',
  'hover-darken',
  'hover-lighten'
] as const;
export const SIZES = ['x-small', 'small', 'default', 'large'] as const;
export const VARIANTS = [
  'disabled',
  'flat',
  'square',
  'round',
  'outline',
  'no-padding',
  'dense',
  'fill',
  'active',
  'no-border',
  'even-padding',
  ...COLORS,
  ...SIZES
] as const;

const propTypes = {
  /**
   * Fired every time the button is clicked
   */
  onClick: PropTypes.func,
  /**
   * Fired when `selectable` and the user just clicked the button,
   * passes the new state the button is being changed to
   */
  onChange: PropTypes.func,
  /**
   * Renders the button as a link
   */
  href: PropTypes.string,
  /**
   * Decides whether the button is a selectable one
   * @deprecated Change the color dynamically instead
   */
  isSelectable: PropTypes.bool,
  /**
   * Whether the button is selected, state must be handled by caller
   * @deprecated Change the color dynamically instead
   */
  isSelected: PropTypes.bool,
  /**
   * Whether the button is selected by default
   * @deprecated Change the color dynamically instead
   */
  isSelectedByDefault: PropTypes.bool,
  /**
   * Disables all functionality of the button
   */
  isDisabled: PropTypes.bool,
  /**
   * Additional classnames to apply
   */
  className: PropTypes.string,
  /**
   * Allows specifying an icon
   * @deprecated Use `Button.Icon` instead
   */
  icon: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string.isRequired),
    PropTypes.shape({
      iconName: PropTypes.string.isRequired,
      prefix: PropTypes.string.isRequired
    })
  ]),
  /**
   * Allows specifying where the icon would be placed, if `right` icon will be on the right
   * @deprecated Use `Button.Icon` instead
   */
  alignIcon: PropTypes.oneOf(['left', 'right', false]),
  /**
   * Specifies the size of the icon
   * @deprecated Use `Button.Icon` instead
   */
  iconSize: PropTypes.oneOf(ICON_SIZES),
  /**
   * Decides the variant of the button
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf(VARIANTS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(VARIANTS), PropTypes.bool]))
  ]),
  /**
   * The color of the button
   */
  color: PropTypes.oneOf(COLORS),
  /**
   * Allow customization of the margin around the button. See `Spacer` Component
   */
  spacing: PropTypes.shape({
    top: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    bottom: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    left: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    right: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    x: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    y: PropTypes.oneOfType([PropTypes.number, PropTypes.bool])
  }),
  /**
   * Custom styles to apply, use sparingly
   */
  style: PropTypes.shape({}),
  /**
   * Inner content of the button
   */
  children: PropTypes.node,
  /**
   * Render onto an element type
   */
  component: PropTypes.elementType,
  /**
   * Whether onclick to preventDefault on event
   */
  shouldPreventDefault: PropTypes.bool,
  /**
   * Whether onclick to stopPropagation on event
   */
  shouldStopPropagation: PropTypes.bool
};

const defaultProps = {
  children: false,
  className: '',
  variant: 'default',
  color: 'white',
  icon: null,
  alignIcon: 'left',
  iconSize: ICON_SIZES[0],
  isSelected: null,
  isSelectable: false,
  isSelectedByDefault: false,
  isDisabled: false,
  href: null,
  onClick: null,
  onChange: () => null,
  component: 'button',
  shouldPreventDefault: true,
  shouldStopPropagation: true,
  spacing: {},
  style: {}
};

export interface ButtonOverrideProps {
  component?: React.ElementType;
  onChange?: (currentValue: boolean) => void;
  onClick?:
    | React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>
    | React.KeyboardEventHandler<HTMLButtonElement | HTMLAnchorElement>
    | null;
  children?:
    | React.ReactNode
    | ((state: {
        isSelected: boolean;
        toggleSelected: () => void;
        setSelected: (value: boolean) => void;
      }) => React.ReactNode);
}

export type ButtonProps = InferPropTypes<
  typeof propTypes,
  typeof defaultProps,
  ButtonOverrideProps
> &
  // [RL] We must omit ButtonOVerrideProps properties or they would be merged
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonOverrideProps>;

/**
 * Amazing! Isn't the best button you've ever laid your eyes on?
 *
 * It can have 2 purposes:
 *
 * - *button only*: The button will notify the caller of when the
 * button was clicked through the button's `onClick` handler.
 * - *form input*: The button will maintain an active/selected state internal and
 * notify the callee that a change has been made through the button's `onChange` handler.
 */
const Button: React.FC<ButtonProps> = React.forwardRef(
  (
    {
      onClick: onClickFromProps,
      onChange,
      children,
      isSelectable,
      isSelected: isSelectedFromProps,
      icon,
      alignIcon: alignIconFromProps,
      iconSize,
      isDisabled,
      href,
      className,
      variant: variants,
      color,
      component: Component,
      shouldPreventDefault,
      shouldStopPropagation,
      isSelectedByDefault,
      style,
      spacing,
      ...props
    }: ButtonProps,
    ref
  ) => {
    const spacingStyles = useSpacing(spacing, { style });
    const {
      value: isSelected,
      toggle: toggleSelected,
      set: setSelected
    } = useBoolean(isSelectedByDefault!, {
      onChange,
      overrideWith: isSelectedFromProps
    });

    const onClick = useEventCallback(onClickFromProps!);
    const onClickButton = useCallback(
      (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        if (onClick.current !== null && shouldPreventDefault) {
          e.preventDefault();
        }

        if (shouldStopPropagation) {
          e.stopPropagation();
        }

        if (isDisabled) {
          return;
        }

        if (isSelectable) {
          toggleSelected();
        }
        if (onClick.current !== null) {
          onClick(e);
        }
      },
      [
        isSelectable,
        isDisabled,
        shouldPreventDefault,
        shouldStopPropagation,
        onClick,
        toggleSelected
      ]
    );
    const childrenEl = useMemo(
      () =>
        typeof children === 'function'
          ? children({ isSelected, toggleSelected, setSelected })
          : children,
      [children, isSelected, setSelected, toggleSelected]
    );
    const hasIcon = icon !== null;

    const BaseComponent: React.ElementType = href ? 'a' : Component!;
    let iconEl;

    let alignIcon;

    if (children) {
      alignIcon = alignIconFromProps;
    }

    if (hasIcon) {
      iconEl = (
        <span className={addVariants('aglmd-button__icon__container', styles)}>
          <Icon
            icon={icon!}
            className={addVariants('aglmd-button__icon', styles)}
            size={iconSize}
          />
        </span>
      );

      if (!children) {
        alignIcon = false;
      }
    }

    return (
      <BaseComponent
        ref={ref}
        className={classnames(
          addVariants(
            'aglmd-button',
            styles,
            variants,
            color,
            isDisabled && 'disabled',
            isSelectable && isSelected && 'active',
            !children && icon && 'icon-only',
            hasIcon ? 'has-icon' : 'no-icon',
            `icon-${alignIcon}`
          ),
          className
        )}
        onClick={onClickButton}
        style={spacingStyles}
        tabIndex={0}
        onKeyPress={(e: React.KeyboardEvent<HTMLButtonElement | HTMLAnchorElement>) => {
          if (e.key === 'Enter') {
            onClickButton(e as unknown as React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>);
          }
        }}
        href={href || undefined}
        {...props}
      >
        {alignIcon === 'left' ? iconEl : childrenEl}
        {alignIcon === 'left' ? childrenEl : iconEl}
      </BaseComponent>
    );
  }
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(Button as any).propTypes = propTypes;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(Button as any).defaultProps = defaultProps;
export default Button;
